import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Description, Dropdown, Label, Table } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { cn } from '@/utils/cnUtils';
import type { StatementColumns, StatementRow } from '../../statementMeta';

type StatementViewProps = {
    /** Descriptive table label, e.g. "Balance sheet" / "Profit and loss". */
    ariaLabel: string;
    rows: StatementRow[];
    /**
     * Column headers, written from the **server's** windows. A Balance Sheet
     * reads "As of 30/07/2026", a P&L reads its range — never a hardcoded
     * "this period", which a position does not have.
     */
    columns: StatementColumns;
    /** Second drill hop — open the journal filtered to one real account. */
    onViewLedger: (accountId: string) => void;
    /** Literal testid prefix, e.g. `balance-sheet` / `profit-loss`. */
    testIdPrefix: string;
    /** Footer note, e.g. "All figures in Georgian Lari (₾), rounded to the tetri." */
    footerNote: string;
    /** Footer stamp, e.g. "Generated 24/07/2026 14:20". */
    generatedText: string;
};

/**
 * Shared statement renderer. Draws whatever line list the server returns as a
 * real, navigable table — nested by depth (28px/level), subtotals and totals
 * heavier with rules, negatives in the danger token, nil cells an en dash.
 *
 * The OSS `Table` compound rather than Pro's `DataGrid`: every row here carries
 * its own styling (`rowClassName` distinguishes line from subtotal from total)
 * and its own indentation and `aria-level`. `DataGrid` exposes no per-row
 * className, so it cannot express a statement's row kinds; `Table.Row` takes
 * them. Statements also render fully expanded and print to A4, so nothing here
 * is virtualized.
 *
 * Drill-through follows the composition the server sent, and only that — see
 * {@link StatementDrill}.
 */
export const StatementView: React.FC<StatementViewProps> = ({
    ariaLabel,
    rows,
    columns,
    onViewLedger,
    testIdPrefix,
    footerNote,
    generatedText,
}) => {
    const { t } = useTranslation('pages');
    const showComparatives = columns.prior !== null;

    return (
        <div>
            <div {...getNodeTestIdProp(`${testIdPrefix}-table`)}>
                <Table>
                    <Table.ScrollContainer>
                        <Table.Content
                            aria-label={ariaLabel}
                            className="text-sm"
                        >
                            <Table.Header>
                                <Table.Column id="account" isRowHeader>
                                    {t('statements.columns.account')}
                                </Table.Column>
                                <Table.Column
                                    id="amount"
                                    {...getNodeTestIdProp(
                                        `${testIdPrefix}-column-amount`,
                                    )}
                                >
                                    {columns.amount}
                                </Table.Column>
                                {showComparatives ? (
                                    <Table.Column
                                        id="prior"
                                        {...getNodeTestIdProp(
                                            `${testIdPrefix}-column-prior`,
                                        )}
                                    >
                                        {columns.prior}
                                    </Table.Column>
                                ) : null}
                                {showComparatives ? (
                                    <Table.Column id="delta">
                                        {columns.delta}
                                    </Table.Column>
                                ) : null}
                            </Table.Header>
                            <Table.Body>
                                {rows.map((row) => (
                                    <Table.Row
                                        aria-level={row.ariaLevel}
                                        className={cn(
                                            'group',
                                            row.rowClassName,
                                            row.drillAccounts.length > 0 &&
                                                'hover:bg-surface-secondary/60',
                                        )}
                                        id={row.line.id}
                                        key={row.line.id}
                                        {...getNodeTestIdProp(
                                            `${testIdPrefix}-row-${row.line.id}`,
                                        )}
                                    >
                                        <Table.Cell>
                                            <span
                                                className="flex items-center gap-2"
                                                style={{
                                                    paddingInlineStart:
                                                        row.paddingInlineStart,
                                                }}
                                            >
                                                {row.code ? (
                                                    <span className="shrink-0 text-xs tabular-nums text-muted">
                                                        {row.code}
                                                    </span>
                                                ) : null}
                                                <span
                                                    className={
                                                        row.labelClassName
                                                    }
                                                >
                                                    {row.label}
                                                </span>
                                                {row.hasWarning ? (
                                                    <Icon
                                                        aria-label={t(
                                                            'statements.warnings.lineMarker',
                                                        )}
                                                        className="text-warning"
                                                        icon="gravity-ui:triangle-exclamation"
                                                    />
                                                ) : null}
                                                <StatementDrill
                                                    onViewLedger={onViewLedger}
                                                    row={row}
                                                    testIdPrefix={testIdPrefix}
                                                />
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell
                                            className={row.amountClassName}
                                            {...getNodeTestIdProp(
                                                `${testIdPrefix}-amount-${row.line.id}`,
                                            )}
                                        >
                                            {row.amountText}
                                        </Table.Cell>
                                        {showComparatives ? (
                                            <Table.Cell
                                                className={row.priorClassName}
                                                {...getNodeTestIdProp(
                                                    `${testIdPrefix}-prior-${row.line.id}`,
                                                )}
                                            >
                                                {row.priorText}
                                            </Table.Cell>
                                        ) : null}
                                        {showComparatives ? (
                                            <Table.Cell
                                                className={row.deltaClassName}
                                                {...getNodeTestIdProp(
                                                    `${testIdPrefix}-delta-${row.line.id}`,
                                                )}
                                            >
                                                {row.deltaText}
                                            </Table.Cell>
                                        ) : null}
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Content>
                    </Table.ScrollContainer>
                </Table>
            </div>

            <div className="mt-3 flex flex-wrap justify-between gap-2 px-1 text-xs text-muted">
                <span>{footerNote}</span>
                <span {...getNodeTestIdProp(`${testIdPrefix}-generated`)}>
                    {generatedText}
                </span>
            </div>
        </div>
    );
};

const DRILL_CLASS_NAME =
    'ml-1 inline-flex h-auto min-h-0 cursor-[var(--cursor-interactive,pointer)] items-center whitespace-nowrap p-0 text-xs font-medium text-accent opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100';

type StatementDrillProps = {
    row: StatementRow;
    onViewLedger: (accountId: string) => void;
    testIdPrefix: string;
};

/**
 * The drill affordance, or nothing at all.
 *
 * Three cases, driven entirely by the composition the server sent:
 *
 * - **one account** — a direct "View in ledger" link;
 * - **several accounts** — the same affordance as a menu of those accounts,
 *   because the journal filters by a single account at a time and the reader
 *   should be the one to choose which. Each item is a real ledger account id
 *   with its own contribution to the line;
 * - **no accounts** — `null`. Every `SUBTOTAL` and `COMPUTED` line, the result
 *   for the period included, lands here. A control that cannot act is the dead
 *   link this screen used to have, and shipping a second kind of it would be
 *   worse than the first.
 */
const StatementDrill: React.FC<StatementDrillProps> = ({
    row,
    onViewLedger,
    testIdPrefix,
}) => {
    const { t } = useTranslation('pages');
    const accounts = row.drillAccounts;
    const first = accounts[0];

    if (!first) {
        return null;
    }

    if (accounts.length === 1) {
        return (
            <button
                aria-label={t('statements.viewLedgerFor', {
                    account: row.label,
                })}
                className={DRILL_CLASS_NAME}
                onClick={() => onViewLedger(first.accountId)}
                type="button"
                {...getNodeTestIdProp(
                    `${testIdPrefix}-view-ledger-${row.line.id}`,
                )}
            >
                {t('statements.viewLedger')}
            </button>
        );
    }

    return (
        <Dropdown>
            <Button
                aria-label={t('statements.viewLedgerFor', {
                    account: row.label,
                })}
                className={DRILL_CLASS_NAME}
                variant="ghost"
                {...getNodeTestIdProp(
                    `${testIdPrefix}-view-ledger-${row.line.id}`,
                )}
            >
                {t('statements.viewLedger')}
            </Button>
            <Dropdown.Popover>
                <Dropdown.Menu
                    onAction={(key) => onViewLedger(String(key))}
                    {...getNodeTestIdProp(
                        `${testIdPrefix}-view-ledger-menu-${row.line.id}`,
                    )}
                >
                    {accounts.map((account) => (
                        <Dropdown.Item
                            id={account.accountId}
                            key={account.accountId}
                            textValue={`${account.accountCode} ${account.accountName}`}
                        >
                            <div className="flex flex-col">
                                <Label>{account.accountName}</Label>
                                <Description>{account.accountCode}</Description>
                            </div>
                            <span className="ms-auto ps-4 text-xs tabular-nums text-muted">
                                {account.amount}
                            </span>
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown>
    );
};
